import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, MapPin, Users, BookOpen, Sparkles, Building2, Calendar, Compass, Package, Wrench } from "lucide-react";
import { focusAreas, diasporaFeatures, organisations, pillars, heroImg, lagosImg } from "../data/mock";

// Icons for the "What we do" pillars — user asked for icons instead of photos for
// Consultancy, Skills-Based Support, and Physical Resource Provision.
const PILLAR_ICONS = [Compass, Wrench, Package];
import { publicApi } from "../lib/api";

// Verified Nigerian photography URLs (from Nigerian photographers: Everest Agomate, wakky Paul,
// Divine Effiong, Itoro Bernard, Ben Iwara) — sourced fresh, none overlap with the pillars section.
const HERO_PORTRAIT = heroImg;                                                                         // Nigerian schoolchildren (verified stable)
const HERO_SECONDARY = "https://images.unsplash.com/photo-1627423896085-e3e694d88e40?auto=format&fit=crop&q=80&w=1200"; // Port Harcourt classroom (verified Nigerian)
const CARD_CONNECT = "https://images.unsplash.com/photo-1665006852027-5243f26e78b6?auto=format&fit=crop&q=80&w=1200";   // Nigerian man (Lagos)
const CARD_LEARN = "https://images.unsplash.com/photo-1622540777427-5d4e6a08ed48?auto=format&fit=crop&q=80&w=1200";     // Itoro Bernard portrait
const CARD_CONTRIBUTE = "https://images.unsplash.com/photo-1783012039560-5690190a9b9b?auto=format&fit=crop&q=80&w=1200"; // Ghanaian/African smiling man

const Marquee = () => {
  const items = [...focusAreas, ...focusAreas, ...focusAreas];
  return (
    <div className="overflow-hidden py-6 border-y" style={{ borderColor: "rgba(18,52,40,0.12)", background: "var(--paper)" }}>
      <div className="marquee-track flex gap-10 whitespace-nowrap">
        {items.map((f, i) => (
          <span key={i} className="flex items-center gap-6 text-[22px] md:text-[28px] font-serif-i" style={{ color: "var(--forest)" }}>
            {f}
            <span style={{ color: "var(--gold)" }} className="text-xl">✦</span>
          </span>
        ))}
      </div>
    </div>
  );
};

const Home = () => {
  const [live, setLive] = useState({ organisations: 10, youth: "10,350+", diaspora: null, prelaunch: false });

  useEffect(() => {
    Promise.all([
      publicApi.get("/settings").then((r) => r.data).catch(() => ({})),
      publicApi.get("/organisations").then((r) => r.data).catch(() => []),
      publicApi.get("/stats/diaspora").then((r) => r.data).catch(() => ({})),
    ]).then(([settings, orgs, diasporaStats]) => {
      const totalYouth = orgs.reduce((acc, o) => {
        const n = parseInt(String(o.youth_reached || "").replace(/[^0-9]/g, ""), 10);
        return acc + (isNaN(n) ? 0 : n);
      }, 0);
      setLive({
        organisations: orgs.length || 10,
        youth: totalYouth ? `${totalYouth.toLocaleString()}+` : "10,350+",
        diaspora: typeof diasporaStats.approved_members_count === "number"
          ? diasporaStats.approved_members_count
          : null,
        prelaunch: !!settings.prelaunch_mode,
      });
    });
  }, []);

  const impactCells = [
    { label: "Organisations supported", value: live.organisations, icon: Building2 },
    { label: "Youth reached", value: live.youth, icon: Users },
    // The Diaspora Members counter is now LIVE from the backend and shown
    // regardless of prelaunch mode. Suppressed only until the first API load returns.
    ...(live.diaspora === null ? [] : [{ label: "Diaspora members", value: live.diaspora, icon: BookOpen }]),
  ];

  return (
    <div>
      {/* HERO — beige editorial with photo card on the right (no full-bleed image behind headline) */}
      <section className="relative overflow-hidden" style={{ background: "var(--beige)" }}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-14 md:pt-20 pb-14 md:pb-20 grid md:grid-cols-2 gap-10 md:gap-16 items-center">
          <div className="fade-up">
            <div className="section-eyebrow mb-6" style={{ color: "rgba(18,52,40,0.6)" }}>
              An Independent Ministry of Education
            </div>
            <h1 className="hero-title text-[56px] md:text-[80px] lg:text-[96px] leading-[1.02]">
              Building<br />
              <span className="hand-underline" style={{ color: "var(--gold)" }}>Nigeria&rsquo;s</span><br />
              Future<br />
              Workforce.
            </h1>
            <p className="mt-8 text-[16px] md:text-[17px] leading-relaxed max-w-[520px]" style={{ color: "rgba(18,52,40,0.85)" }}>
              We strengthen the organisations equipping Nigerian young people with the skills, mindset,
              and opportunity to solve real problems &mdash; and inherit the future.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/join" className="inline-flex items-center gap-2 px-5 py-3 rounded-md btn-gold text-[14px] font-medium">
                Join the Network <ArrowUpRight size={16} />
              </Link>
              <Link to="/partner" className="inline-flex items-center gap-2 px-5 py-3 rounded-md btn-outline text-[14px] font-medium">
                Partner With Us <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>

          {/* Photo collage — primary photo card + secondary b&w classroom card, no illustrated map */}
          <div className="relative fade-up delay-2 flex justify-center md:justify-end min-h-[580px]">
            <div
              className="absolute z-0 rounded-md overflow-hidden bg-white p-2 shadow-[0_20px_40px_-20px_rgba(18,52,40,0.35)]"
              style={{ transform: "rotate(-4deg)", width: "270px", right: "300px", top: "0px" }}
            >
              <img
                src={HERO_SECONDARY}
                alt="Nigerian classroom, Port Harcourt"
                className="w-full h-[220px] object-cover"
                style={{ filter: "grayscale(1) contrast(1.05)" }}
                onError={(e) => { e.currentTarget.src = heroImg; }}
              />
            </div>
            <div
              className="relative rounded-md overflow-hidden bg-white p-3 shadow-[0_30px_60px_-25px_rgba(18,52,40,0.35)]"
              style={{ transform: "rotate(1.5deg)", maxWidth: "440px", zIndex: 1 }}
            >
              <img
                src={HERO_PORTRAIT}
                alt="Nigerian schoolgirl in uniform"
                className="w-full h-[520px] object-cover"
                onError={(e) => { e.currentTarget.src = heroImg; }}
              />
            </div>
          </div>
        </div>

        {/* IMPACT BAR — dark forest strip spanning full width, mockup-accurate */}
        <div style={{ background: "var(--forest)" }} data-testid="impact-strip">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-7 grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-10">
            {impactCells.map(({ label, value, icon: Icon }) => (
              <div key={label} className="flex items-center gap-4">
                <div
                  className="w-10 h-10 rounded-md flex items-center justify-center shrink-0"
                  style={{ background: "rgba(205,163,78,0.15)", color: "var(--gold)" }}
                >
                  <Icon size={18} />
                </div>
                <div>
                  <div className="hero-title text-[28px] md:text-[32px] leading-none" style={{ color: "var(--cream)" }}>{value}</div>
                  <div className="text-[12px] mt-1" style={{ color: "rgba(255,246,226,0.75)" }}>{label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Marquee />

      {/* A practical way to contribute — three cards each with a photo, matching the mockup */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 md:py-28">
        <div className="text-center mb-14">
          <h2 className="classic-title text-[40px] md:text-[60px] leading-tight max-w-[900px] mx-auto">
            A practical way to <span className="hand-underline orange"><span className="font-serif-i" style={{ color: "var(--orange)" }}>contribute</span></span> your expertise.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { Icon: Users, tint: "var(--forest)", title: "Connect", titleStyle: {}, desc: "Discover and connect with a community of Nigerian diaspora professionals.", img: CARD_CONNECT, fallback: heroImg },
            { Icon: BookOpen, tint: "var(--gold)", title: "Learn", titleStyle: {}, desc: "Access curated masterclasses, conversations and events.", img: CARD_LEARN, fallback: heroImg },
            { Icon: Sparkles, tint: "var(--orange)", title: "Contribute", titleStyle: { fontStyle: "italic" }, desc: "Use your skills to support organisations through defined, practical projects.", img: CARD_CONTRIBUTE, fallback: heroImg },
          ].map(({ Icon, tint, title, titleStyle, desc, img, fallback }, i) => (
            <div key={i} className="editorial-card overflow-hidden flex flex-col">
              <div className="p-7">
                <div className="w-11 h-11 rounded-md flex items-center justify-center mb-5" style={{ background: tint, color: "#fff" }}>
                  <Icon size={20} />
                </div>
                <h3 className="text-[30px] mb-2" style={{ color: tint, fontFamily: "'Instrument Serif', Georgia, serif", ...titleStyle }}>{title}</h3>
                <p className="text-[15px] leading-relaxed" style={{ color: "rgba(18,52,40,0.78)" }}>{desc}</p>
              </div>
              <div className="mt-auto aspect-[16/10] overflow-hidden">
                <img src={img} alt={title} className="w-full h-full object-cover"
                     onError={(e) => { if (e.currentTarget.src !== fallback) e.currentTarget.src = fallback; }} />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Who we are (kept, unchanged structure) */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-20 md:pb-28">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="section-eyebrow" style={{ color: "rgba(18,52,40,0.6)" }}>01 · Who we are</div>
          </div>
          <div className="md:col-span-8">
            <h2 className="classic-title text-[40px] md:text-[64px] leading-[1.02] max-w-[820px]">
              Not another <span className="font-serif-i" style={{ color: "var(--orange)" }}>school reform</span> project.
            </h2>
            <div className="mt-8 grid md:grid-cols-2 gap-8 text-[16px] leading-relaxed max-w-[900px]" style={{ color: "rgba(18,52,40,0.85)" }}>
              <p>Vemoye exists because we believe education should do more than produce grades. It should produce problem-solvers, innovators, leaders, and citizens capable of building the future.</p>
              <p>Every society is shaped by the education system it creates. If we want a different Nigeria, we must build a different education system &mdash; one designed for the century we&rsquo;re actually living in.</p>
            </div>
            <Link to="/about" className="inline-flex items-center gap-2 mt-8 text-[14px] link-underline" style={{ color: "var(--forest)" }}>
              Read our vision <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Problem */}
      <section style={{ background: "var(--paper)" }}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 md:py-32 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="section-eyebrow" style={{ color: "rgba(18,52,40,0.6)" }}>02 · The Problem</div>
          </div>
          <div className="md:col-span-8">
            <h2 className="classic-title text-[40px] md:text-[64px] leading-[1.02] max-w-[900px]">
              Students learn information &mdash; but not how to <span className="font-serif-i" style={{ color: "var(--orange)" }}>build</span>.
            </h2>
            <div className="mt-8 grid md:grid-cols-2 gap-8 text-[16px] leading-relaxed" style={{ color: "rgba(18,52,40,0.85)" }}>
              <p>Too many education systems are focused on academic performance rather than real-world outcomes. Young people leave school with certificates but without the ability to solve problems, create opportunity, or lead the organisations that will shape their future.</p>
              <p>Meanwhile, thousands of Nigerian organisations are already doing incredible work in STEM, entrepreneurship, leadership, and skills training. What they lack isn&rsquo;t ideas &mdash; it&rsquo;s capacity.</p>
            </div>
          </div>
        </div>
      </section>

      {/* What we do */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 md:py-32">
        <div className="grid md:grid-cols-12 gap-10 mb-14">
          <div className="md:col-span-4">
            <div className="section-eyebrow" style={{ color: "rgba(18,52,40,0.6)" }}>03 · What we do</div>
          </div>
          <div className="md:col-span-8">
            <h2 className="classic-title text-[40px] md:text-[64px] leading-[1.02] max-w-[900px]">
              We strengthen the organisations <span className="font-serif-i" style={{ color: "var(--orange)" }}>already building it</span>.
            </h2>
            <p className="mt-6 text-[16px] leading-relaxed max-w-[700px]" style={{ color: "rgba(18,52,40,0.85)" }}>
              Rather than starting new programmes, Vemoye amplifies proven ones. We give existing education-focused organisations the systems, capital, and diaspora expertise they need to serve more young people.
            </p>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {pillars.map((p, i) => {
            const Icon = PILLAR_ICONS[i] || Compass;
            return (
              <div key={i} className="editorial-card p-8 flex flex-col h-full">
                <div
                  className="w-14 h-14 rounded-md flex items-center justify-center mb-6"
                  style={{ background: "rgba(205,163,78,0.15)", color: "var(--gold)" }}
                >
                  <Icon size={26} />
                </div>
                <div className="font-serif-i text-[24px] mb-1" style={{ color: "var(--gold)" }}>{p.roman}</div>
                <h3 className="text-[24px] mb-3 classic-title" style={{ color: "var(--forest)" }}>{p.title}</h3>
                <p className="text-[15px] leading-relaxed" style={{ color: "rgba(18,52,40,0.75)" }}>{p.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Diaspora */}
      <section style={{ background: "var(--forest)", color: "var(--cream)" }}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 md:py-32 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="section-eyebrow" style={{ color: "rgba(255,246,226,0.6)" }}>04 · Diaspora</div>
            <div className="mt-6 flex gap-3 flex-wrap">
              <Link to="/join" className="inline-flex items-center gap-2 px-4 py-2 rounded-md btn-gold text-[14px] font-medium">
                Apply to join <ArrowUpRight size={14} />
              </Link>
              <Link to="/diaspora" className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-[14px] border border-white/25 hover:bg-white/5" style={{ color: "var(--cream)" }}>
                How it works
              </Link>
            </div>
          </div>
          <div className="md:col-span-8">
            <h2 className="classic-title text-[40px] md:text-[64px] leading-[1.02] max-w-[900px]" style={{ color: "var(--cream)" }}>
              Two hours a week. <span className="font-serif-i" style={{ color: "var(--gold)" }}>Four weeks.</span> Once a year.
            </h2>
            <p className="mt-6 text-[16px] leading-relaxed max-w-[700px]" style={{ color: "rgba(255,246,226,0.82)" }}>
              Nigeria&rsquo;s diaspora is one of its greatest untapped resources. Most want to contribute &mdash; they just don&rsquo;t know where to start. Vemoye creates a practical, high-leverage path.
            </p>
            <div className="grid md:grid-cols-2 gap-6 mt-12">
              {diasporaFeatures.map((f) => (
                <div key={f.n} className="pt-5 border-t border-white/15">
                  <div className="text-[13px] mb-2" style={{ color: "rgba(255,246,226,0.55)" }}>{f.n}</div>
                  <h4 className="text-[20px] mb-2 classic-title" style={{ color: "var(--cream)" }}>{f.title}</h4>
                  <p className="text-[14px] leading-relaxed" style={{ color: "rgba(255,246,226,0.78)" }}>{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Ecosystem */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 md:py-32">
        <div className="flex items-end justify-between mb-10 gap-4 flex-wrap">
          <div>
            <div className="section-eyebrow" style={{ color: "rgba(18,52,40,0.6)" }}>05 · Ecosystem</div>
            <h2 className="classic-title text-[40px] md:text-[64px] leading-[1.02] mt-4">
              Organisations <span className="font-serif-i" style={{ color: "var(--orange)" }}>we support</span>
            </h2>
          </div>
          <Link to="/organisations" className="inline-flex items-center gap-2 text-[14px] link-underline" style={{ color: "var(--forest)" }}>
            View all <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {organisations.slice(1).map((o) => (
            <div key={o.id} className="editorial-card p-6">
              <div className="flex items-center gap-2 text-[12px] mb-6" style={{ color: "rgba(18,52,40,0.6)" }}>
                <span className="px-2 py-1 rounded" style={{ background: "var(--beige)", color: "var(--forest)" }}>{o.tag}</span>
                <span className="flex items-center gap-1"><MapPin size={12} />{o.city}</span>
              </div>
              <h3 className="text-[22px] mb-2 classic-title" style={{ color: "var(--forest)" }}>{o.name}</h3>
              <p className="text-[14px] leading-relaxed mb-6" style={{ color: "rgba(18,52,40,0.75)" }}>{o.desc}</p>
              <div className="flex items-center justify-between pt-4 border-t" style={{ borderColor: "rgba(18,52,40,0.08)" }}>
                <div>
                  <div className="text-[11px]" style={{ color: "rgba(18,52,40,0.6)" }}>Youth reached</div>
                  <div className="text-[22px]" style={{ color: "var(--forest)", fontFamily: "'Instrument Serif', Georgia, serif" }}>{o.reached}</div>
                </div>
                <ArrowUpRight size={18} style={{ color: "var(--gold)" }} />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Vision quote */}
      <section className="relative">
        <div className="absolute inset-0">
          <img src={lagosImg} alt="Lagos" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(18,52,40,0.9), rgba(18,52,40,0.75))" }} />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-28 md:py-40" style={{ color: "var(--cream)" }}>
          <div className="section-eyebrow mb-6" style={{ color: "rgba(255,246,226,0.6)" }}>06 · The Vision</div>
          <blockquote className="font-serif-i text-[36px] md:text-[64px] leading-[1.1] max-w-[1100px]" style={{ color: "var(--cream)" }}>
            &ldquo;The purpose of education is not to prepare children for exams. It is to prepare them for life, work, leadership &mdash; and nation-building.&rdquo;
          </blockquote>
          <Link to="/about" className="inline-flex items-center gap-2 mt-10 px-5 py-3 rounded-md btn-gold text-[14px] font-medium">
            Read the manifesto <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>

      {/* JOIN THE VEMOYE NETWORK strip */}
      <section style={{ background: "var(--forest)", color: "var(--cream)" }} data-testid="join-strip">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 md:py-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h3 className="classic-title text-[30px] md:text-[42px] leading-tight" style={{ color: "var(--cream)" }}>
              Join the <span className="font-serif-i" style={{ color: "var(--gold)" }}>Vemoye Network</span>
            </h3>
            <p className="mt-2 text-[14px] md:text-[15px]" style={{ color: "rgba(255,246,226,0.8)" }}>
              Applications are now open for our waiting list.
            </p>
          </div>
          <Link to="/join" className="inline-flex items-center gap-2 px-6 py-3 rounded-md btn-gold text-[14px] font-medium">
            <Calendar size={14} /> Apply to Join <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
